/**
 * DateSelectionState — Single source of truth for the date filter.
 * Eliminates ambiguous combinations of selectedDate / selectedEndDate / selectedTimeFrame.
 */

import { TimeFrameOption } from '../components/TimeFrameSelector';

export type SelectionMode = 'SINGLE_DATE' | 'DATE_RANGE';

export interface DateSelectionState {
	/** Current selection mode */
	mode: SelectionMode;
	/** ISO date string — used only when mode === 'SINGLE_DATE' */
	selectedDate?: string;
	/** ISO date string — range start, used only when mode === 'DATE_RANGE' */
	startDate?: string;
	/** ISO date string — range end, used only when mode === 'DATE_RANGE' */
	endDate?: string;
	/** Which time frame pill produced this range (undefined if manual or none) */
	activeTimeFrame?: TimeFrameOption;
}

// ─── Factory Helpers (pure, testable) ────────────────────────────────────────

/**
 * Create a DATE_RANGE selection from a time frame computation.
 */
export function createDateRangeSelection(
	startDate: string,
	endDate: string,
	activeTimeFrame: TimeFrameOption
): DateSelectionState {
	return { mode: 'DATE_RANGE', startDate, endDate, activeTimeFrame };
}

/**
 * Create a SINGLE_DATE selection (clears any active time frame).
 */
export function createSingleDateSelection(selectedDate: string): DateSelectionState {
	return { mode: 'SINGLE_DATE', selectedDate };
}

/**
 * Format the display value for the date filter based on DateSelectionState.
 * Uses the same locale formatting as the rest of the app.
 */
export function formatDateFilterValue(state: DateSelectionState): string {
	if (state.mode === 'DATE_RANGE' && state.startDate && state.endDate) {
		return `${formatIsoDate(state.startDate)} - ${formatIsoDate(state.endDate)}`;
	}
	if (state.mode === 'SINGLE_DATE' && state.selectedDate) {
		return formatIsoDate(state.selectedDate);
	}
	return '';
}

/**
 * Format an ISO date string (YYYY-MM-DD) to a human-readable format (e.g. "Jul 14, 2026").
 */
function formatIsoDate(iso: string): string {
	const parts = iso.split('-');
	const date = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
	return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}
